import React, { useState, useEffect } from 'react'
import '../styles/dashboard.css'
import '../styles/students.css'
import '../styles/leads.css'
import Sidebar from '../components/Sidebar'
import Header from '../components/Header'
import MeetingScheduler from '../components/MeetingScheduler'
import CountryMultiSelect from '../components/CountryMultiSelect'
import { ViewModeWrap } from '../components/ViewRecordModal'
import { REFERRED_BY_OPTIONS, requiresReferredByName } from '../constants/referredByOptions'
import StudentRegistrationIdField from '../components/StudentRegistrationIdField'
import { validateStudentRegistrationId } from '../utils/studentRegistrationId'
import QualificationFields from '../components/QualificationFields'
import {
    emptyQualificationsState,
    parseQualificationsFromLead,
    buildQualificationsForApi,
    summarizeQualificationInstitutes
} from '../constants/leadQualifications'
import { LEAD_PROGRAM_OPTIONS, normalizeLeadProgram } from '../constants/programOptions'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faUserPlus,
    faArrowLeft,
    faSave,
    faUserCheck,
    faHistory,
    faUserTie,
    faTimes,
    faCalendarAlt,
    faCheckCircle,
    faClock,
    faExclamationTriangle,
    faTrash,
    faPlus,
    faSearch,
    faGlobe,
    faCreditCard,
    faFlag,
    faFileInvoiceDollar,
    faLink,
    faMoneyBillWave,
    faExternalLinkAlt,
    faUserGraduate,
    faReceipt,
    faPrint,
} from "@fortawesome/free-solid-svg-icons";

const LEAD_STATUS_OPTIONS = [
    { value: 'new', label: 'New' },
    { value: 'contacted', label: 'Contacted' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'converted', label: 'Converted' },
    { value: 'closed', label: 'Closed' },
];

const normalizeLeadStatus = (value) => {
    if (!value) return 'new';
    const key = String(value).trim().toLowerCase().replace(/[\s-]+/g, '_');
    return LEAD_STATUS_OPTIONS.some(opt => opt.value === key) ? key : 'new';
};

const leadStatusLabel = (value) => {
    const match = LEAD_STATUS_OPTIONS.find(opt => opt.value === normalizeLeadStatus(value));
    return match ? match.label : 'New';
};

const API_URL = process.env.REACT_APP_API_URL || 'https://unilinkapi-production.up.railway.app';

const ADMISSION_TEST_LABELS = {
    ielts: 'IELTS',
    toefl: 'TOEFL',
    sat: 'SAT',
    gre_gmat: 'GRE/GMAT'
};

const DEFAULT_ADMISSION_TESTS = {
    ielts: { attempted: '', score: '', date: '' },
    toefl: { attempted: '', score: '', date: '' },
    sat: { attempted: '', score: '', date: '' },
    gre_gmat: { attempted: '', score: '', date: '' }
};

const parseAdmissionTestsFromLead = (lead) => {
    const defaults = JSON.parse(JSON.stringify(DEFAULT_ADMISSION_TESTS));
    if (!lead.admission_tests) return defaults;
    try {
        const parsed = typeof lead.admission_tests === 'string' ? JSON.parse(lead.admission_tests) : lead.admission_tests;
        return {
            ielts: { ...defaults.ielts, ...parsed.ielts },
            toefl: { ...defaults.toefl, ...parsed.toefl },
            sat: { ...defaults.sat, ...parsed.sat },
            gre_gmat: { ...defaults.gre_gmat, ...parsed.gre_gmat }
        };
    } catch (e) {
        return defaults;
    }
};

const mapInvoiceStatusToLead = (status) => {
    if (status === 'Paid') return 'Paid';
    if (status === 'Partially Paid') return 'Partially Paid';
    return 'Unpaid';
};

const canRegisterFromPayment = (status) => ['Paid', 'Partially Paid'].includes(status);

const getPaymentStatusStyle = (status) => {
    const map = {
        'Paid':           { background: '#dcfce7', color: '#166534', border: '1px solid #86efac' },
        'Pending':        { background: '#fef9c3', color: '#854d0e', border: '1px solid #fde047' },
        'Partially Paid': { background: '#dbeafe', color: '#1e40af', border: '1px solid #93c5fd' },
    };
    return map[status] || { background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db' };
};

const parseCountries = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const resolveLeadCountries = (countries, countriesOther) => {
    const list = Array.isArray(countries) ? countries : parseCountries(countries);
    const other = (countriesOther || '').trim();
    return list
        .map(c => {
            const name = String(c).trim();
            if (name.toLowerCase() === 'other') return other || null;
            return name;
        })
        .filter(Boolean);
};

const formatLeadCountries = (countries, countriesOther) =>
    resolveLeadCountries(countries, countriesOther).join(', ') || '-';

const buildApplicantInfoFromLead = (lead) => {
    const nameParts = (lead.full_name || '').trim().split(/\s+/).filter(Boolean);
    const countryNames = resolveLeadCountries(lead.countries_of_interest, lead.countries_other);
    return {
        name: nameParts[0] || '',
        middle_name: nameParts.length > 2 ? nameParts.slice(1, -1).join(' ') : '',
        surname: nameParts.length > 1 ? nameParts[nameParts.length - 1] : '',
        email: lead.email || '',
        mobile: lead.phone || '',
        address: lead.address || '',
        country: countryNames.length ? countryNames.join(', ') : '',
        course: lead.interest || '',
        program: normalizeLeadProgram(lead.program || ''),
        source_inquiry: lead.referred_by || '',
        status: 'Active',
    };
};

export default function EditLead({ recordId, viewOnly = false } = {}) {
    const { id: paramId } = useParams();
    const id = recordId || paramId;
    const navigate = useNavigate();

    const [leadData, setLeadData] = useState({
        full_name: '',
        email: '',
        phone: '',
        address: '',
        interest: '',
        program: '',
        institute_name: '',
        comments: '',
        status: 'new',
        is_registered: false,
        countries_of_interest: [],
        countries_other: '',
        qualifications: emptyQualificationsState(),
        referred_by: '',
        referred_by_name: '',
        admission_tests: JSON.parse(JSON.stringify(DEFAULT_ADMISSION_TESTS))
    });

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [showRegisterModal, setShowRegisterModal] = useState(false);
    const [registering, setRegistering] = useState(false);
    const [followUpHistory, setFollowUpHistory] = useState([]);
    const [leadChanges, setLeadChanges] = useState([]);

    const [allCounselors, setAllCounselors] = useState([]);
    const [assignedCounselors, setAssignedCounselors] = useState([]);
    const [showCounselorModal, setShowCounselorModal] = useState(false);
    const [counselorSearchTerm, setCounselorSearchTerm] = useState('');
    const [assigningCounselor, setAssigningCounselor] = useState(false);

    const [meetings, setMeetings] = useState([]);
    const [showMeetingModal, setShowMeetingModal] = useState(false);
    const [selectedCounselorForMeeting, setSelectedCounselorForMeeting] = useState(null);

    const [countries, setCountries] = useState([]);

    const [paymentStatus, setPaymentStatus] = useState('Unpaid');
    const [regFormError, setRegFormError] = useState('');
    const [registrationIdSuffix, setRegistrationIdSuffix] = useState('');

    // â”€â”€ Lead payment / invoice linking â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [leadInvoices, setLeadInvoices] = useState([]);
    const [leadInvoicesLoading, setLeadInvoicesLoading] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [paymentForm, setPaymentForm] = useState({
      paymentStatus: 'Pending',
      installmentAmount: '',
      paymentDate: '',
      paymentType: 'advance', // 'advance' | 'installment'
    });
    const [invoicePayments, setInvoicePayments] = useState([]);
    const [invoiceInstallments, setInvoiceInstallments] = useState([]);
    const [updatingPayment, setUpdatingPayment] = useState(false);
    const [paymentModalError, setPaymentModalError] = useState('');
    const [linkingInvoice, setLinkingInvoice] = useState(false);

    useEffect(() => {
        if (id) {
            fetchLeadData();
            fetchLeadHistory();
            fetchAssignedCounselors();
            fetchMeetings();
        }
    }, [id]);

    useEffect(() => {
        if (showCounselorModal) fetchAllCounselors();
    }, [showCounselorModal]);

    useEffect(() => {
        fetchCountries();
    }, []);

    const fetchLeadData = async () => {
        setLoading(true);
        setError('');
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/leads/${id}`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            const data = await response.json();
            if (data.success) {
                setLeadData({
                    ...data.lead,
                    countries_of_interest: parseCountries(data.lead.countries_of_interest),
                    countries_other: data.lead.countries_other || '',
                    status: normalizeLeadStatus(data.lead.status),
                    program: normalizeLeadProgram(data.lead.program || ''),
                    institute_name: data.lead.institute_name || '',
                    interest: data.lead.interest || '',
                    comments: data.lead.counsellor_notes || data.lead.comments || '',
                    referred_by: data.lead.referred_by || '',
                    referred_by_name: data.lead.referred_by_name || '',
                    qualifications: parseQualificationsFromLead(data.lead),
                    admission_tests: parseAdmissionTestsFromLead(data.lead)
                });
                setPaymentStatus(data.lead.payment_status || 'Unpaid');
                if (data.lead.invoice_id) {
                    fetch(`${API_URL}/finance/invoices/${data.lead.invoice_id}`)
                        .then(r => r.json())
                        .then(invData => {
                            if (invData.success && invData.invoice) {
                                setSelectedInvoice(invData.invoice);
                            }
                        })
                        .catch(() => {});
                }
            } else {
                setError(data.message || 'Failed to fetch lead data');
            }
        } catch (err) {
            console.error('Error fetching lead:', err);
            setError('Network error. Please check your connection.');
        } finally {
            setLoading(false);
        }
    };

    const fetchLeadInvoices = async () => {
        setLeadInvoicesLoading(true);
        setPaymentModalError('');
        try {
            const res = await fetch(`${API_URL}/leads/${id}/invoices`);
            const data = await res.json();
            if (data.success) {
                setLeadInvoices(data.invoices || []);
                setPaymentStatus(data.paymentStatus || 'Unpaid');
                if (data.linkedInvoice) {
                    await loadInvoicePaymentContext(data.linkedInvoice);
                } else {
                    setSelectedInvoice(null);
                    setInvoicePayments([]);
                    setInvoiceInstallments([]);
                    setPaymentForm({
                        paymentStatus: 'Pending',
                        installmentAmount: '',
                        paymentDate: new Date().toISOString().slice(0, 10),
                        paymentType: 'advance',
                    });
                }
            } else {
                setPaymentModalError(data.message || 'Failed to load invoices');
            }
        } catch (err) {
            setPaymentModalError('Network error. Please try again.');
        } finally {
            setLeadInvoicesLoading(false);
        }
    };

    const invoiceHasInstallmentPlan = (inv, installments = []) =>
        !!(inv && (inv.has_installment_plan === 1 || inv.has_installment_plan === true || (installments && installments.length > 0)));

    const invoiceHasAdvance = (inv, payments = []) => {
        const advanceAmt = parseFloat(inv?.advance_amount) || 0;
        if (advanceAmt > 0) return true;
        return (payments || []).some(p => String(p.notes || '').trim().toLowerCase() === 'advance');
    };

    const defaultPaymentTypeForInvoice = (inv, payments = [], installments = []) => {
        if (invoiceHasInstallmentPlan(inv, installments)) return 'installment';
        if (!invoiceHasAdvance(inv, payments)) return 'advance';
        return 'installment';
    };

    const loadInvoicePaymentContext = async (invoice) => {
        let detailInvoice = invoice;
        let payments = [];
        let installments = [];
        try {
            const detailRes = await fetch(`${API_URL}/finance/invoices/${invoice.id}`);
            const detailData = await detailRes.json();
            if (detailData.success) {
                detailInvoice = { ...invoice, ...detailData.invoice };
                payments = detailData.payments || [];
                installments = detailData.installments || [];
            }
        } catch {
            try {
                const payRes = await fetch(`${API_URL}/finance/invoices/${invoice.id}/payments`);
                const payData = await payRes.json();
                payments = payData.success ? (payData.payments || []) : [];
            } catch {
                payments = [];
            }
        }

        setSelectedInvoice(detailInvoice);
        setInvoicePayments(payments);
        setInvoiceInstallments(installments);
        setPaymentForm({
            paymentStatus: detailInvoice.payment_status || 'Pending',
            installmentAmount: '',
            paymentDate: new Date().toISOString().slice(0, 10),
            paymentType: defaultPaymentTypeForInvoice(detailInvoice, payments, installments),
        });
        return { detailInvoice, payments, installments };
    };

    const openPaymentModal = () => {
        setShowPaymentModal(true);
        fetchLeadInvoices();
    };

    const handleLinkInvoice = async (invoice) => {
        setLinkingInvoice(true);
        setPaymentModalError('');
        try {
            const res = await fetch(`${API_URL}/leads/${id}/payment`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ invoiceId: invoice.id })
            });
            const data = await res.json();
            if (data.success) {
                setPaymentStatus(data.paymentStatus);
                await loadInvoicePaymentContext(invoice);
                await fetchLeadInvoices();
                setLeadData(prev => ({ ...prev, payment_status: data.paymentStatus, invoice_id: invoice.id }));
            } else {
                setPaymentModalError(data.message || 'Failed to link invoice');
            }
        } catch (err) {
            setPaymentModalError('Network error. Please try again.');
        } finally {
            setLinkingInvoice(false);
        }
    };

    const handleUpdateInvoicePayment = async () => {
        if (!selectedInvoice) return;
        const amount = parseFloat(paymentForm.installmentAmount);
        const hasAmount = isFinite(amount) && amount > 0;
        setUpdatingPayment(true);
        setPaymentModalError('');
        try {
            let data;
            if (hasAmount) {
                const hasPlan = invoiceHasInstallmentPlan(selectedInvoice, invoiceInstallments);
                const hasAdvance = invoiceHasAdvance(selectedInvoice, invoicePayments);
                // No plan and no advance yet → always treat as advance
                const asAdvance = hasPlan
                    ? paymentForm.paymentType === 'advance'
                    : !hasAdvance;

                const res = await fetch(`${API_URL}/finance/invoices/${selectedInvoice.id}/payments`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        amount,
                        paymentDate: paymentForm.paymentDate,
                        paymentStatus: paymentForm.paymentStatus,
                        paymentMethod: 'cash',
                        asAdvance,
                        paymentType: asAdvance ? 'advance' : 'installment',
                        notes: asAdvance ? 'Advance' : null,
                    })
                });
                data = await res.json();
                if (data.success) {
                    if (data.payments) setInvoicePayments(data.payments);
                    if (data.installments) setInvoiceInstallments(data.installments);
                    setSelectedInvoice(prev => prev ? {
                        ...prev,
                        paid_amount: data.paidAmount,
                        payment_status: data.paymentStatus,
                        advance_amount: data.advanceAmount != null ? data.advanceAmount : prev.advance_amount,
                        has_installment_plan: data.hasInstallmentPlan != null
                            ? (data.hasInstallmentPlan ? 1 : 0)
                            : prev.has_installment_plan,
                    } : prev);
                    setPaymentForm(prev => ({
                        ...prev,
                        installmentAmount: '',
                        paymentStatus: data.paymentStatus || prev.paymentStatus,
                        paymentType: defaultPaymentTypeForInvoice(
                            {
                                ...selectedInvoice,
                                advance_amount: data.advanceAmount,
                                has_installment_plan: data.hasInstallmentPlan,
                            },
                            data.payments || invoicePayments,
                            data.installments || invoiceInstallments
                        ),
                    }));
                }
            } else {
                const res = await fetch(`${API_URL}/finance/invoices/${selectedInvoice.id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ paymentStatus: paymentForm.paymentStatus })
                });
                data = await res.json();
            }
            if (data.success) {
                const status = data.paymentStatus || paymentForm.paymentStatus;
                const leadStatus = mapInvoiceStatusToLead(status);
                setPaymentStatus(leadStatus);
                setLeadData(prev => ({ ...prev, payment_status: leadStatus }));
                await fetchLeadInvoices();
            } else {
                setPaymentModalError(data.message || 'Failed to update payment');
            }
        } catch (err) {
            setPaymentModalError('Network error. Please try again.');
        } finally {
            setUpdatingPayment(false);
        }
    };

    const fetchLeadHistory = async () => {
        try {
            const response = await fetch(`${API_URL}/leads/${id}/history`);
            const data = await response.json();
            if (data.success) {
                setFollowUpHistory(data.followUps || []);
                setLeadChanges(data.changes || []);
            }
        } catch (err) {
            console.error('Error fetching lead history:', err);
        }
    };

    const fetchAllCounselors = async () => {
        try {
            const response = await fetch(`${API_URL}/counselors`);
            const data = await response.json();
            if (data.success) setAllCounselors(data.counselors);
        } catch (err) {
            console.error('Error fetching counselors:', err);
        }
    };

    const fetchAssignedCounselors = async () => {
        try {
            const response = await fetch(`${API_URL}/leads/${id}/counselors`);
            const data = await response.json();
            if (data.success) setAssignedCounselors(data.counselors || []);
        } catch (err) {
            console.error('Error fetching assigned counselors:', err);
        }
    };

    const fetchMeetings = async () => {
        try {
            const response = await fetch(`${API_URL}/leads/${id}/meetings`);
            const data = await response.json();
            if (data.success) setMeetings(data.meetings || []);
        } catch (err) {
            console.error('Error fetching meetings:', err);
        }
    };

    const fetchCountries = async () => {
        try {
            const response = await fetch(`${API_URL}/countries`);
            const data = await response.json();
            if (data.success) {
                setCountries(data.countries.filter(c => c.status === 'Active'));
            }
        } catch (err) {
            console.error('Error fetching countries:', err);
        }
    };

    const formatLeadId = (leadId) => `LEAD${String(leadId).padStart(5, '0')}`;

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setLeadData(prev => ({ ...prev, [name]: value }));
    };

    const handleCountryToggle = (country) => {
        setLeadData(prev => {
            const current = prev.countries_of_interest || [];
            const updated = current.includes(country)
                ? current.filter(c => c !== country)
                : [...current, country];
            return {
                ...prev,
                countries_of_interest: updated,
                countries_other: updated.includes('Other') ? prev.countries_other : ''
            };
        });
    };

    const handleAdmissionTestChange = (testKey, field, value) => {
        setLeadData(prev => {
            const prevRow = prev.admission_tests?.[testKey] || { attempted: '', score: '', date: '' };
            const nextRow = { ...prevRow, [field]: value };
            if (field === 'score' || field === 'date') {
                nextRow.attempted = (nextRow.score || nextRow.date) ? 'Yes' : '';
            }
            return {
                ...prev,
                admission_tests: {
                    ...prev.admission_tests,
                    [testKey]: nextRow
                }
            };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (viewOnly) return;
        if (!leadData.referred_by?.trim()) {
            setError('Please select how the lead heard about us (Referred By).');
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }
        if (requiresReferredByName(leadData.referred_by) && !leadData.referred_by_name?.trim()) {
            setError('Please enter the Referred By (Name).');
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }
        setSaving(true);
        setError('');
        try {
            const response = await fetch(`${API_URL}/leads/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    fullName: leadData.full_name,
                    email: leadData.email,
                    phone: leadData.phone,
                    address: leadData.address,
                    interest: leadData.interest,
                    program: leadData.program,
                    instituteName: summarizeQualificationInstitutes(leadData.qualifications),
                    counsellorNotes: leadData.comments,
                    comments: leadData.comments,
                    status: leadData.status,
                    countriesOfInterest: leadData.countries_of_interest,
                    countriesOther: leadData.countries_other,
                    qualifications: buildQualificationsForApi(leadData.qualifications),
                    referredBy: leadData.referred_by,
                    referredByName: leadData.referred_by_name,
                    admissionTests: leadData.admission_tests
                })
            });
            const data = await response.json();
            if (data.success) {
                alert('Lead updated successfully!');
                navigate('/leads');
            } else {
                setError(data.message || 'Failed to update lead');
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        } catch (err) {
            console.error('Error updating lead:', err);
            setError('Network error. Please check your connection.');
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } finally {
            setSaving(false);
        }
    };

    const handleAssignCounselor = async (counselorId) => {
        setAssigningCounselor(true);
        try {
            const response = await fetch(`${API_URL}/leads/${id}/assign-counselor`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ counselorId })
            });
            const data = await response.json();
            if (data.success) {
                alert('Counselor assigned successfully!');
                fetchAssignedCounselors();
                setShowCounselorModal(false);
            } else {
                alert(data.message || 'Failed to assign counselor');
            }
        } catch (err) {
            alert('Network error. Please try again.');
        } finally {
            setAssigningCounselor(false);
        }
    };

    const handleRemoveCounselor = async (counselorId) => {
        if (!window.confirm('Are you sure you want to remove this counselor?')) return;
        try {
            const response = await fetch(`${API_URL}/leads/${id}/counselors/${counselorId}`, { method: 'DELETE' });
            const data = await response.json();
            if (data.success) {
                alert('Counselor removed successfully!');
                fetchAssignedCounselors();
            } else {
                alert(data.message || 'Failed to remove counselor');
            }
        } catch (err) {
            alert('Network error. Please try again.');
        }
    };

    const handleRegisterAsStudent = async () => {
        setRegFormError('');
        const applicantInfo = buildApplicantInfoFromLead(leadData);

        if (!canRegisterFromPayment(paymentStatus)) {
            setRegFormError('Payment must be Partially Paid or Paid before registering the student.');
            return;
        }
        if (!applicantInfo.name.trim()) {
            setRegFormError('Lead must have a first name.');
            return;
        }
        if (!applicantInfo.surname.trim()) {
            setRegFormError('Lead must have a last name in the full name field.');
            return;
        }
        if (!applicantInfo.email.trim()) {
            setRegFormError('Lead must have an email address.');
            return;
        }
        if (!applicantInfo.mobile.trim()) {
            setRegFormError('Lead must have a phone number.');
            return;
        }
        if (!applicantInfo.address.trim()) {
            setRegFormError('Lead must have an address.');
            return;
        }
        const idCheck = validateStudentRegistrationId(registrationIdSuffix);
        if (!idCheck.valid) {
            setRegFormError(idCheck.message);
            return;
        }

        setRegistering(true);
        setError('');
        try {
            const response = await fetch(`${API_URL}/leads/${id}/register-student`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ applicantInfo, studentId: idCheck.value })
            });
            const data = await response.json();
            if (data.success) {
                setShowRegisterModal(false);
                navigate(`/edit-student/${data.userId}`);
            } else {
                setRegFormError(data.message || 'Failed to register student');
            }
        } catch (err) {
            setRegFormError('Network error. Please check your connection.');
        } finally {
            setRegistering(false);
        }
    };

    const handleScheduleMeeting = (counselor) => {
        setSelectedCounselorForMeeting(counselor);
        setShowMeetingModal(true);
    };

    const getMeetingStatusColor = (status) => {
        const colors = { 'Scheduled': 'meeting-scheduled', 'Completed': 'meeting-completed', 'Cancelled': 'meeting-cancelled' };
        return colors[status] || 'meeting-scheduled';
    };

    const filteredCounselors = allCounselors.filter(counselor => {
        const isAssigned = assignedCounselors.some(ac => ac.id === counselor.id);
        const matches = counselor.name.toLowerCase().includes(counselorSearchTerm.toLowerCase()) ||
            counselor.email.toLowerCase().includes(counselorSearchTerm.toLowerCase()) ||
            counselor.counselor_id.toLowerCase().includes(counselorSearchTerm.toLowerCase());
        return !isAssigned && matches;
    });

    // Include initial lead form submission as follow-up #1 (same convention as the Follow-ups badge)
    const displayFollowUpHistory = [
        ...followUpHistory.map((followUp) => ({
            ...followUp,
            displayNumber: (Number(followUp.follow_up_number) || 0) + 1,
        })),
        ...(leadData.created_at ? [{
            id: 'initial-submission',
            displayNumber: 1,
            followed_up_at: leadData.created_at,
            notes: followUpHistory.length === 0
                ? (leadData.comments || leadData.counsellor_notes || null)
                : null,
            purpose_of_visit: followUpHistory.length === 0
                ? (leadData.purpose_of_visit || null)
                : null,
            isInitial: true,
        }] : []),
    ];

    if (loading) {
        return (
            <ViewModeWrap viewOnly={viewOnly}>
                <Sidebar />
                <div className="main-content" id="mainContent">
                    <Header />
                    <div style={{ textAlign: 'center', padding: '40px' }}><p>Loading lead data...</p></div>
                </div>
            </ViewModeWrap>
        );
    }

    return (
        <ViewModeWrap viewOnly={viewOnly}>
            <Sidebar />
            <div className="main-content" id="mainContent">
                <Header />
                <div className="leads-container students-container">
                    <div className="leads-header no-print">
                        <div className="header-left">
                            <h1>
                                <FontAwesomeIcon icon={faUserPlus} className="page-icon" />
                                {viewOnly ? 'Lead Details' : 'Edit Lead'}
                            </h1>
                        </div>
                        <div className="header-right">
                            <button type="button" className="btn-secondary" onClick={() => window.print()} style={{ marginRight: '10px' }}>
                                <FontAwesomeIcon icon={faPrint} /> Print
                            </button>
                            <Link to="/leads" className="btn-secondary">
                                <FontAwesomeIcon icon={faArrowLeft} />
                                Back to Leads
                            </Link>
                        </div>
                    </div>

                    {error && (
                        <div className="no-print" style={{ background: '#fee2e2', color: '#991b1b', padding: '16px 20px', borderRadius: '12px', marginBottom: '20px', border: '1px solid #fecaca' }}>
                            {error}
                        </div>
                    )}

                    <div className="lead-summary-card">
                        <div className="summary-row">
                            <div className="summary-item">
                                <span className="summary-label">Lead ID:</span>
                                <span className="lead-id-display">{formatLeadId(leadData.id)}</span>
                            </div>
                            <div className="summary-item no-print">
                                <span className="summary-label">Walk-in Date:</span>
                                <span className="lead-id-display">{new Date(leadData.created_at).toLocaleDateString()}</span>
                            </div>
                            <div className="summary-item no-print">
                                <span className="summary-label">Status:</span>
                                <span className="lead-id-display">{leadStatusLabel(leadData.status)}</span>
                            </div>
                            <div className="summary-item no-print">
                                <span className="summary-label">Follow-ups:</span>
                                <span className="lead-id-display" style={{ background: '#F47B20', color: 'white', padding: '4px 12px', borderRadius: '20px', fontSize: '14px', fontWeight: 'bold' }}>
                                    {(Number(leadData.follow_up_count) || 0) + 1}
                                </span>
                            </div>
                            {!!leadData.is_registered && leadData.registered_at && (
                                <div className="summary-item no-print">
                                    <span className="summary-label">Registered On:</span>
                                    <span className="lead-id-display">{new Date(leadData.registered_at).toLocaleDateString()}</span>
                                </div>
                            )}
                        </div>
                        <div className="lead-summary-actions no-print">
                            {!!leadData.is_registered && leadData.student_user_id && (
                                <Link
                                    to={`/edit-student/${leadData.student_user_id}`}
                                    className="btn-primary btn-register-large"
                                    style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                                >
                                    <FontAwesomeIcon icon={faUserGraduate} />
                                    View Student Profile
                                </Link>
                            )}
                            <button
                                type="button"
                                className="btn-primary btn-register-large"
                                onClick={() => setShowCounselorModal(true)}
                                disabled={leadData.is_registered}
                            >
                                <FontAwesomeIcon icon={faUserTie} />
                                Assign Counselor
                            </button>
                            {!leadData.is_registered ? (
                                <>
                                    <button
                                        type="button"
                                        className="btn-secondary btn-register-large"
                                        onClick={openPaymentModal}
                                        title="Payment Status"
                                    >
                                        <FontAwesomeIcon icon={faMoneyBillWave} />
                                        Payment
                                        <span className={`lead-payment-pill ${paymentStatus === 'Paid' ? 'paid' : paymentStatus === 'Partially Paid' ? 'partial' : ''}`}>{paymentStatus}</span>
                                    </button>
                                    <button
                                        type="button"
                                        className="btn-primary btn-register-large"
                                        onClick={() => { setRegFormError(''); setRegistrationIdSuffix(''); setShowRegisterModal(true); }}
                                        disabled={!canRegisterFromPayment(paymentStatus)}
                                        style={{
                                            opacity: canRegisterFromPayment(paymentStatus) ? 1 : 0.55,
                                            cursor: canRegisterFromPayment(paymentStatus) ? 'pointer' : 'not-allowed'
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faUserCheck} />
                                        Register Student
                                    </button>
                                </>
                            ) : (
                                <div className="registered-status">
                                    <FontAwesomeIcon icon={faUserCheck} className="registered-icon" />
                                    <span>Already Registered</span>
                                </div>
                            )}
                        </div>
                    </div>

                    <form className="lead-form lead-form-container student-form" onSubmit={handleSubmit}>
                        <div className="form-section">
                            <h2 className="section-title">Lead Information</h2>
                            <div className="row">
                                <div className="col-md-12">
                                    <div className="form-group">
                                        <label htmlFor="full_name">Full Name <span className="required">*</span></label>
                                        <input type="text" id="full_name" name="full_name" className="input-field form-control"
                                            value={leadData.full_name} onChange={handleInputChange} required />
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="email">Email Address <span className="required">*</span></label>
                                        <input type="email" id="email" name="email" className="input-field form-control"
                                            value={leadData.email} onChange={handleInputChange} required />
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="phone">Phone Number <span className="required">*</span></label>
                                        <input type="tel" id="phone" name="phone" className="input-field form-control"
                                            value={leadData.phone} onChange={handleInputChange} required />
                                    </div>
                                </div>

                                <div className="col-md-12">
                                    <div className="form-group">
                                        <label htmlFor="address">Complete Address <span className="required">*</span></label>
                                        <textarea id="address" name="address" className="input-field form-control" rows="3"
                                            value={leadData.address} onChange={handleInputChange} required></textarea>
                                    </div>
                                </div>

                                {/* Countries of Interest */}
                                <div className="col-md-12">
                                    <div className="form-group">
                                        <label className="section-label-orange">
                                            <FontAwesomeIcon icon={faGlobe} style={{ marginRight: '8px' }} />
                                            Countries of Interest
                                        </label>
                                        <CountryMultiSelect
                                            countries={countries}
                                            selected={leadData.countries_of_interest || []}
                                            onToggle={handleCountryToggle}
                                            otherValue={leadData.countries_other || ''}
                                            onOtherChange={(value) => setLeadData(prev => ({ ...prev, countries_other: value }))}
                                            disabled={viewOnly}
                                            readOnly={viewOnly}
                                        />
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="program">Level of Study</label>
                                        <select id="program" name="program" className="input-field form-control"
                                            value={normalizeLeadProgram(leadData.program || '')} onChange={handleInputChange}>
                                            <option value="">Select a level of study</option>
                                            {LEAD_PROGRAM_OPTIONS.map((opt) => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="interest">Area of Interest <span className="required">*</span></label>
                                        <input type="text" id="interest" name="interest" className="input-field form-control"
                                            value={leadData.interest || ''} onChange={handleInputChange} required
                                            placeholder="e.g. Business, Engineering, Medicine..." />
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="status">Lead Status <span className="required">*</span></label>
                                        <select id="status" name="status" className="input-field form-control"
                                            value={leadData.status || 'new'} onChange={handleInputChange} required>
                                            {LEAD_STATUS_OPTIONS.map(opt => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {/* Current Level of Qualification */}
                                <div className="col-md-12">
                                    <div className="form-group">
                                        <label className="section-label-orange">Current Level of Qualification</label>
                                        <QualificationFields
                                            qualifications={leadData.qualifications}
                                            onChange={(qualifications) => setLeadData(prev => ({ ...prev, qualifications }))}
                                            disabled={viewOnly}
                                            readOnly={viewOnly}
                                        />
                                    </div>
                                </div>

                                {/* Referred By */}
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="referred_by">Source of Inquiry <span className="required">*</span></label>
                                        <select
                                            id="referred_by"
                                            name="referred_by"
                                            className="input-field form-control"
                                            value={leadData.referred_by || ''}
                                            onChange={(e) => {
                                                handleInputChange(e);
                                                if (!requiresReferredByName(e.target.value)) {
                                                    setLeadData(prev => ({ ...prev, referred_by_name: '' }));
                                                }
                                            }}
                                            required
                                        >
                                            <option value="">How did you hear about us?</option>
                                            {REFERRED_BY_OPTIONS.map(option => (
                                                <option key={option} value={option}>{option}</option>
                                            ))}
                                            {leadData.referred_by === 'Person Name' && (
                                                <option value="Person Name">Person Name</option>
                                            )}
                                        </select>
                                    </div>
                                </div>

                                {requiresReferredByName(leadData.referred_by) && (
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="referred_by_name">Referred By (Name) <span className="required">*</span></label>
                                        <input
                                            type="text"
                                            id="referred_by_name"
                                            name="referred_by_name"
                                            className="input-field form-control"
                                            value={leadData.referred_by_name || ''}
                                            onChange={handleInputChange}
                                            required
                                            placeholder="Enter the referrer's name"
                                        />
                                    </div>
                                </div>
                                )}

                                {/* English / Admission Tests */}
                                <div className="col-md-12">
                                    <div className="form-group">
                                        <label className="section-label-orange">English / Admission Tests</label>
                                        <div className="admission-tests-table">
                                            <div className="admission-tests-header">
                                                <span>Test</span>
                                                <span>Score</span>
                                                <span>Date/Month/Year of Test</span>
                                            </div>
                                            {Object.entries(ADMISSION_TEST_LABELS).map(([key, label]) => (
                                                <div key={key} className="admission-test-row">
                                                    <span className="test-name">{label}</span>
                                                    <div className="admission-field">
                                                        <span className="admission-field-label">Score</span>
                                                        <input
                                                            type="text"
                                                            placeholder={key === 'ielts' ? 'e.g. 6.5' : key === 'toefl' ? 'e.g. 90' : key === 'sat' ? 'e.g. 1200' : 'e.g. 310'}
                                                            value={leadData.admission_tests?.[key]?.score || ''}
                                                            onChange={(e) => handleAdmissionTestChange(key, 'score', e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="admission-field">
                                                        <span className="admission-field-label">Date/Month/Year of Test</span>
                                                        <input
                                                            type="text"
                                                            placeholder="e.g. 2024, Mar 2024, or 15 Mar 2024"
                                                            value={leadData.admission_tests?.[key]?.date || ''}
                                                            onChange={(e) => handleAdmissionTestChange(key, 'date', e.target.value)}
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-12">
                                    <div className="form-group">
                                        <label htmlFor="comments">Counsellor Notes</label>
                                        <textarea id="comments" name="comments" className="input-field form-control" rows="4"
                                            value={leadData.comments || ''} onChange={handleInputChange}
                                            placeholder="Write your notes here..."></textarea>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="form-actions no-print">
                            <Link to="/leads" className="btn-secondary">Cancel</Link>
                            <button type="submit" className="btn-primary" disabled={saving}>
                                <FontAwesomeIcon icon={faSave} />
                                {saving ? 'Updating...' : 'Update Lead'}
                            </button>
                        </div>
                    </form>

                    {/* Assigned Counselors Section */}
                    <div className="form-section no-print" style={{ marginTop: '32px' }}>
                        <div className="section-header">
                            <h2 className="section-title">
                                <FontAwesomeIcon icon={faUserTie} style={{ marginRight: '10px' }} />
                                Assigned Counselors
                            </h2>
                        </div>
                        {assignedCounselors.length > 0 ? (
                            <div className="row g-3">
                                {assignedCounselors.map((counselor) => (
                                    <div key={counselor.id} className="col-md-6">
                                        <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px', background: 'white' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
                                                <div>
                                                    <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>{counselor.name}</h4>
                                                    <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#6b7280' }}>{counselor.counselor_id}</p>
                                                </div>
                                                <button type="button" className="btn-icon btn-delete"
                                                    onClick={() => handleRemoveCounselor(counselor.id)}
                                                    disabled={leadData.is_registered}>
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </button>
                                            </div>
                                            <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}><strong>Email:</strong> {counselor.email}</div>
                                            <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}><strong>Experience:</strong> {counselor.experience}</div>
                                            <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '15px' }}><strong>Expertise:</strong> {counselor.expertise}</div>
                                            <button type="button" className="btn-primary" style={{ width: '100%' }}
                                                onClick={() => handleScheduleMeeting(counselor)}>
                                                <FontAwesomeIcon icon={faCalendarAlt} />
                                                Schedule Meeting
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="no-data-message">
                                <FontAwesomeIcon icon={faUserTie} className="no-data-icon" />
                                <p>No counselors assigned yet.</p>
                                {!leadData.is_registered && (
                                    <button type="button" className="btn-primary" onClick={() => setShowCounselorModal(true)} style={{ marginTop: '15px' }}>
                                        <FontAwesomeIcon icon={faPlus} />
                                        Assign First Counselor
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Scheduled Meetings Section */}
                    {meetings.length > 0 && (
                        <div className="form-section no-print" style={{ marginTop: '32px' }}>
                            <h2 className="section-title">
                                <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: '10px' }} />
                                Scheduled Meetings
                            </h2>
                            <div className="row g-3">
                                {meetings.map((meeting) => (
                                    <div key={meeting.id} className="col-12">
                                        <div className="meeting-card">
                                            <div className="meeting-header">
                                                <div className="meeting-student">
                                                    <FontAwesomeIcon icon={faUserTie} className="meeting-icon" />
                                                    <div>
                                                        <h4>{meeting.counselor_name}</h4>
                                                        <span className="student-id-small">{meeting.counselor_id}</span>
                                                    </div>
                                                </div>
                                                <span className={`meeting-status-badge ${getMeetingStatusColor(meeting.status)}`}>
                                                    {meeting.status}
                                                </span>
                                            </div>
                                            <div className="meeting-details">
                                                <div className="meeting-info-item">
                                                    <FontAwesomeIcon icon={faCalendarAlt} className="info-icon-small" />
                                                    <span>{new Date(meeting.meeting_date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                                                </div>
                                                <div className="meeting-info-item">
                                                    <FontAwesomeIcon icon={faClock} className="info-icon-small" />
                                                    <span>{meeting.meeting_time} ({meeting.duration_minutes} minutes)</span>
                                                </div>
                                            </div>
                                            {meeting.notes && <div className="meeting-notes"><strong>Notes:</strong> {meeting.notes}</div>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Follow-up History Section */}
                    <div className="form-section no-print" style={{ marginTop: '32px' }}>
                        <h2 className="section-title">
                            <FontAwesomeIcon icon={faHistory} style={{ marginRight: '10px' }} />
                            Follow-up History
                        </h2>
                        {displayFollowUpHistory.length > 0 ? (
                            <div className="table-responsive">
                                <table className="table meeting-history-table">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Follow-up Date</th>
                                            <th>Notes</th>
                                            <th>Purpose Of Visit</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {displayFollowUpHistory.map((followUp) => (
                                                <tr key={followUp.id}>
                                                    <td>
                                                        <span style={{ background: '#F47B20', color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold' }}>
                                                            #{followUp.displayNumber}
                                                        </span>
                                                    </td>
                                                    <td>{new Date(followUp.followed_up_at).toLocaleString()}</td>
                                                    <td>{followUp.notes || 'No notes'}</td>
                                                    <td>{followUp.purpose_of_visit || 'No purpose specified'}</td>
                                                </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="no-data-message">
                                <FontAwesomeIcon icon={faHistory} className="no-data-icon" />
                                <p>No follow-up history available.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
                REGISTER AS STUDENT MODAL
            â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}
            {showRegisterModal && (
                <>
                    <div className="modal-backdrop show" onClick={() => !registering && setShowRegisterModal(false)}></div>
                    <div className="modal show" style={{ display: 'block' }} tabIndex="-1">
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                                <div className="modal-header" style={{
                                    background: 'linear-gradient(135deg, #F47B20 0%, #e8660f 100%)',
                                    padding: '20px 28px', borderBottom: 'none'
                                }}>
                                    <div>
                                        <h5 className="modal-title" style={{ color: 'white', fontSize: '18px', fontWeight: '700', margin: 0 }}>
                                            <FontAwesomeIcon icon={faUserCheck} style={{ marginRight: '10px' }} />
                                            Register as Student
                                        </h5>
                                        <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px', margin: '4px 0 0' }}>
                                            Confirm registration using this lead&apos;s details
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn-close-modal"
                                        onClick={() => !registering && setShowRegisterModal(false)}
                                        disabled={registering}
                                        style={{ color: 'white', background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '8px', width: '36px', height: '36px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    >
                                        <FontAwesomeIcon icon={faTimes} />
                                    </button>
                                </div>

                                <div className="modal-body" style={{ padding: '24px 28px' }}>
                                    {regFormError && (
                                        <div style={{
                                            background: '#fee2e2', color: '#991b1b', padding: '12px 16px',
                                            borderRadius: '10px', marginBottom: '16px', border: '1px solid #fecaca',
                                            fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px'
                                        }}>
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            {regFormError}
                                        </div>
                                    )}

                                    <p style={{ fontSize: '15px', color: '#374151', marginBottom: '16px', lineHeight: 1.6 }}>
                                        Are you sure you want to register <strong>{leadData.full_name}</strong> as a student?
                                        A student profile will be created using the lead&apos;s information, and you will be taken to the student edit page to complete any remaining details.
                                    </p>

                                    <div style={{
                                        background: '#f9fafb', padding: '16px', borderRadius: '10px',
                                        border: '1px solid #e5e7eb', fontSize: '13px', color: '#374151'
                                    }}>
                                        <div style={{ marginBottom: '8px' }}><strong>Name:</strong> {leadData.full_name || '-'}</div>
                                        <div style={{ marginBottom: '8px' }}><strong>Email:</strong> {leadData.email || '-'}</div>
                                        <div style={{ marginBottom: '8px' }}><strong>Phone:</strong> {leadData.phone || '-'}</div>
                                        {summarizeQualificationInstitutes(leadData.qualifications) && <div style={{ marginBottom: '8px' }}><strong>School/College/Institute:</strong> {summarizeQualificationInstitutes(leadData.qualifications)}</div>}
                                        {leadData.address && <div><strong>Address:</strong> {leadData.address}</div>}
                                    </div>

                                    <div style={{ marginTop: '16px' }}>
                                        <StudentRegistrationIdField
                                            value={registrationIdSuffix}
                                            onChange={setRegistrationIdSuffix}
                                            disabled={registering}
                                            inputId="leadRegistrationId"
                                        />
                                    </div>

                                    <div style={{
                                        marginTop: '16px', background: '#f0f9ff', padding: '14px 16px',
                                        borderRadius: '10px', border: '1px solid #bae6fd', fontSize: '13px', color: '#0c4a6e'
                                    }}>
                                        <FontAwesomeIcon icon={faUserCheck} style={{ marginRight: '8px', color: '#0284c7' }} />
                                        Assigned counselors and meetings will be transferred automatically.
                                        {leadData.invoice_id && selectedInvoice && (
                                            <span style={{ display: 'block', marginTop: '6px', color: '#166534' }}>
                                                <FontAwesomeIcon icon={faLink} style={{ marginRight: '6px' }} />
                                                Invoice <strong>{selectedInvoice.invoice_id}</strong> is linked to this lead.
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="modal-footer" style={{ padding: '16px 28px', borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                    <button type="button" className="btn-secondary"
                                        onClick={() => !registering && setShowRegisterModal(false)}
                                        disabled={registering}>
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn-primary"
                                        onClick={handleRegisterAsStudent}
                                        disabled={registering}
                                    >
                                        <FontAwesomeIcon icon={faUserCheck} />
                                        {registering ? 'Registering...' : 'Confirm Registration'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Assign Counselor Modal */}
            {showCounselorModal && (
                <>
                    <div className="modal-backdrop show" onClick={() => !assigningCounselor && setShowCounselorModal(false)}></div>
                    <div className="modal show" style={{ display: 'block' }} tabIndex="-1">
                        <div className="modal-dialog modal-dialog-centered modal-lg">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">
                                        <FontAwesomeIcon icon={faUserTie} style={{ marginRight: '10px' }} />
                                        Assign Counselor to Lead
                                    </h5>
                                    <button type="button" className="btn-close-modal" onClick={() => setShowCounselorModal(false)} disabled={assigningCounselor}>
                                        <FontAwesomeIcon icon={faTimes} />
                                    </button>
                                </div>
                                <div className="modal-body">
                                    <div className="search-box mb-3" style={{ position: 'relative' }}>
                                        <FontAwesomeIcon icon={faSearch} className="search-icon" />
                                        <input type="text" className="input-field"
                                            placeholder="Search counselors by name, email, or ID..."
                                            value={counselorSearchTerm}
                                            onChange={(e) => setCounselorSearchTerm(e.target.value)} />
                                    </div>
                                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                        {filteredCounselors.length > 0 ? (
                                            <div className="row g-3">
                                                {filteredCounselors.map((counselor) => (
                                                    <div key={counselor.id} className="col-12">
                                                        <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '16px', background: 'white' }}
                                                            onMouseEnter={(e) => e.currentTarget.style.borderColor = '#F47B20'}
                                                            onMouseLeave={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                <div style={{ flex: 1 }}>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                                                                        <div style={{ width: '45px', height: '45px', borderRadius: '50%', background: '#F47B20', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '18px' }}>
                                                                            <FontAwesomeIcon icon={faUserTie} />
                                                                        </div>
                                                                        <div>
                                                                            <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>{counselor.name}</h4>
                                                                            <p style={{ margin: '2px 0 0 0', fontSize: '13px', color: '#6b7280' }}>{counselor.counselor_id}</p>
                                                                        </div>
                                                                    </div>
                                                                    <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '6px' }}><strong>Email:</strong> {counselor.email}</div>
                                                                    <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '6px' }}><strong>Experience:</strong> {counselor.experience}</div>
                                                                    <div style={{ fontSize: '13px', color: '#6b7280' }}><strong>Expertise:</strong> {counselor.expertise?.substring(0, 100)}{counselor.expertise?.length > 100 ? '...' : ''}</div>
                                                                </div>
                                                                <button type="button" className="btn-primary"
                                                                    onClick={() => handleAssignCounselor(counselor.id)}
                                                                    disabled={assigningCounselor}
                                                                    style={{ marginLeft: '15px' }}>
                                                                    <FontAwesomeIcon icon={faPlus} />
                                                                    {assigningCounselor ? 'Assigning...' : 'Assign'}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                                                <FontAwesomeIcon icon={faUserTie} style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.3 }} />
                                                <p>{counselorSearchTerm ? 'No counselors found matching your search' : 'All available counselors have been assigned'}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Payment Status Modal */}
            {showPaymentModal && (
                <>
                    <div className="modal-backdrop show" onClick={() => !linkingInvoice && !updatingPayment && setShowPaymentModal(false)}></div>
                    <div className="modal show" style={{ display: 'block' }} tabIndex="-1">
                        <div className="modal-dialog modal-dialog-centered modal-lg">
                            <div className="modal-content" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                                <div className="modal-header" style={{ background: 'linear-gradient(135deg, #fff7ed 0%, #ffffff 100%)', borderBottom: '1px solid #fed7aa' }}>
                                    <h5 className="modal-title" style={{ fontWeight: '700' }}>
                                        <FontAwesomeIcon icon={faMoneyBillWave} style={{ marginRight: '10px', color: '#F47B20' }} />
                                        Payment Status
                                        <span style={{
                                            marginLeft: '12px', fontSize: '12px', padding: '3px 10px', borderRadius: '12px',
                                            ...getPaymentStatusStyle(paymentStatus === 'Unpaid' ? 'Pending' : paymentStatus)
                                        }}>
                                            {paymentStatus}
                                        </span>
                                    </h5>
                                    <button type="button" className="btn-close-modal"
                                        onClick={() => !linkingInvoice && !updatingPayment && setShowPaymentModal(false)}>
                                        <FontAwesomeIcon icon={faTimes} />
                                    </button>
                                </div>
                                <div className="modal-body" style={{ padding: '20px 24px', maxHeight: '70vh', overflowY: 'auto' }}>
                                    {paymentModalError && (
                                        <div style={{
                                            background: '#fef2f2', color: '#b91c1c', padding: '10px 14px',
                                            borderRadius: '8px', marginBottom: '16px', fontSize: '13px',
                                            display: 'flex', alignItems: 'center', gap: '8px'
                                        }}>
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            {paymentModalError}
                                        </div>
                                    )}

                                    <div style={{ background: '#f9fafb', borderRadius: '10px', padding: '12px 14px', marginBottom: '16px', fontSize: '13px' }}>
                                        <div><strong>Lead email:</strong> {leadData.email || '-'}</div>
                                        <div style={{ marginTop: '4px' }}>
                                            <strong>Countries of interest:</strong>{' '}
                                            {formatLeadCountries(leadData.countries_of_interest, leadData.countries_other)}
                                        </div>
                                    </div>

                                    <h6 style={{ fontWeight: '700', marginBottom: '12px', color: '#374151' }}>
                                        <FontAwesomeIcon icon={faFileInvoiceDollar} style={{ marginRight: '8px', color: '#F47B20' }} />
                                        Invoices matching this email
                                    </h6>

                                    {leadInvoicesLoading ? (
                                        <div style={{ textAlign: 'center', padding: '32px', color: '#6b7280' }}>Loading invoices...</div>
                                    ) : leadInvoices.length === 0 ? (
                                        <div style={{ textAlign: 'center', padding: '32px', color: '#6b7280' }}>
                                            <FontAwesomeIcon icon={faFileInvoiceDollar} style={{ fontSize: '32px', opacity: 0.3, marginBottom: '12px' }} />
                                            <p style={{ margin: 0 }}>No student invoices found for <strong>{leadData.email}</strong></p>
                                            <button type="button" className="btn-primary" style={{ marginTop: '16px' }}
                                                onClick={() => {
                                                    setShowPaymentModal(false);
                                                    navigate('/create-invoice', {
                                                        state: {
                                                            fromLead: true,
                                                            leadId: leadData.id,
                                                            studentPrefill: {
                                                                name: leadData.full_name || '',
                                                                email: leadData.email || '',
                                                                mobile: leadData.phone || '',
                                                            },
                                                        },
                                                    });
                                                }}>
                                                <FontAwesomeIcon icon={faFileInvoiceDollar} style={{ marginRight: '8px' }} />
                                                Create Invoice for this Lead
                                            </button>
                                        </div>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
                                            {leadInvoices.map(inv => {
                                                const isSelected = selectedInvoice?.id === inv.id;
                                                const isLinkedElsewhere = inv.is_linked_to_student || inv.is_linked_to_other_lead;
                                                const canSelect = inv.is_linked_to_lead || !isLinkedElsewhere;
                                                return (
                                                    <div key={inv.id} style={{
                                                        border: `2px solid ${isSelected ? '#F47B20' : '#e5e7eb'}`,
                                                        borderRadius: '12px', padding: '14px 16px',
                                                        background: isSelected ? '#fff7ed' : 'white',
                                                        opacity: canSelect || isSelected ? 1 : 0.85
                                                    }}>
                                                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                                                            <div style={{ flex: 1, minWidth: '200px' }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                                    <span style={{ fontWeight: '700', fontSize: '14px' }}>{inv.invoice_id}</span>
                                                                    <span style={{ ...getPaymentStatusStyle(inv.payment_status), padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600' }}>
                                                                        {inv.payment_status}
                                                                    </span>
                                                                    {inv.is_linked_to_lead && (
                                                                        <span style={{ fontSize: '11px', color: '#15803d', fontWeight: '600' }}>Linked</span>
                                                                    )}
                                                                </div>
                                                                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                                                                    {new Date(inv.invoice_date).toLocaleDateString()}
                                                                    &nbsp;·&nbsp; PKR {Number(inv.final_amount || 0).toLocaleString()}
                                                                </div>
                                                                {inv.invoice_countries?.length > 0 && (
                                                                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                                                                        <strong>Countries:</strong> {inv.invoice_countries.join(', ')}
                                                                    </div>
                                                                )}
                                                                {isLinkedElsewhere && !inv.is_linked_to_lead && (
                                                                    <div style={{ fontSize: '12px', color: '#b45309', marginTop: '4px' }}>
                                                                        Already linked to another lead or student
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                                                                <button
                                                                    type="button"
                                                                    className={isSelected ? 'btn-primary' : 'btn-secondary'}
                                                                    style={{ fontSize: '12px' }}
                                                                    disabled={!canSelect || linkingInvoice || isSelected}
                                                                    onClick={() => handleLinkInvoice(inv)}
                                                                >
                                                                    {linkingInvoice ? 'Linking…' : isSelected ? 'Selected' : 'Select'}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {selectedInvoice && (() => {
                                        const hasPlan = invoiceHasInstallmentPlan(selectedInvoice, invoiceInstallments);
                                        const hasAdvance = invoiceHasAdvance(selectedInvoice, invoicePayments);
                                        const autoAdvance = !hasPlan && !hasAdvance;
                                        const showTypeChoice = hasPlan;
                                        const treatingAsAdvance = showTypeChoice
                                            ? paymentForm.paymentType === 'advance'
                                            : autoAdvance;
                                        const enteredAmt = parseFloat(paymentForm.installmentAmount) || 0;
                                        const previewRemaining = Math.max(
                                            0,
                                            Number(selectedInvoice.final_amount || 0)
                                            - Number(selectedInvoice.advance_amount || 0)
                                            - (treatingAsAdvance && enteredAmt > 0 ? enteredAmt : 0)
                                        );
                                        const previewPerInstallment = invoiceInstallments.length > 0
                                            ? Math.round((previewRemaining / invoiceInstallments.length) * 100) / 100
                                            : 0;

                                        return (
                                        <>
                                            <h6 style={{ fontWeight: '700', marginBottom: '12px', color: '#374151', borderTop: '1px solid #f3f4f6', paddingTop: '16px' }}>
                                                <FontAwesomeIcon icon={faCreditCard} style={{ marginRight: '8px', color: '#16a34a' }} />
                                                Update Payment - {selectedInvoice.invoice_id}
                                            </h6>
                                            <div style={{ background: '#f9fafb', borderRadius: '8px', padding: '12px', marginBottom: '16px', fontSize: '13px' }}>
                                                <div style={{ fontWeight: 600 }}>Final Amount: PKR {Number(selectedInvoice.final_amount || 0).toLocaleString()}</div>
                                                <div style={{ color: '#666', marginTop: '4px' }}>
                                                    Paid: <strong style={{ color: '#16a34a' }}>PKR {Number(selectedInvoice.paid_amount || 0).toLocaleString()}</strong>
                                                    {' | '}Remaining: <strong style={{ color: '#ea580c' }}>
                                                        PKR {Math.max(0, Number(selectedInvoice.final_amount || 0) - Number(selectedInvoice.paid_amount || 0)).toLocaleString()}
                                                    </strong>
                                                    {' | '}Payments: <strong>{invoicePayments.length}</strong>
                                                </div>
                                                <div style={{ color: '#666', marginTop: '4px' }}>
                                                    Advance: <strong>PKR {Number(selectedInvoice.advance_amount || 0).toLocaleString()}</strong>
                                                    {' | '}Installment plan: <strong>{hasPlan ? `Yes (${invoiceInstallments.length})` : 'No'}</strong>
                                                </div>
                                                <div style={{ color: '#666', marginTop: '4px' }}>
                                                    Lead status: <strong>{paymentStatus}</strong>
                                                    {' '}(from invoice: {selectedInvoice.payment_status})
                                                </div>
                                            </div>

                                            {autoAdvance && (
                                                <div style={{
                                                    background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af',
                                                    borderRadius: '8px', padding: '10px 12px', marginBottom: '14px', fontSize: '13px'
                                                }}>
                                                    This invoice has no installment plan or advance yet. Any amount you record will be saved as an <strong>Advance Payment</strong>.
                                                </div>
                                            )}

                                            {showTypeChoice && (
                                                <div style={{ marginBottom: '14px' }}>
                                                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#374151', marginBottom: '8px' }}>
                                                        Count this payment as
                                                    </div>
                                                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                                        <button
                                                            type="button"
                                                            onClick={() => setPaymentForm({ ...paymentForm, paymentType: 'installment' })}
                                                            style={{
                                                                flex: '1 1 160px', padding: '10px 12px', borderRadius: '10px', cursor: 'pointer',
                                                                border: `2px solid ${paymentForm.paymentType === 'installment' ? '#F47B20' : '#e5e7eb'}`,
                                                                background: paymentForm.paymentType === 'installment' ? '#fff7ed' : '#fff',
                                                                fontWeight: 700, fontSize: '13px',
                                                                color: paymentForm.paymentType === 'installment' ? '#c2410c' : '#555',
                                                            }}
                                                        >
                                                            Installment
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => setPaymentForm({ ...paymentForm, paymentType: 'advance' })}
                                                            style={{
                                                                flex: '1 1 160px', padding: '10px 12px', borderRadius: '10px', cursor: 'pointer',
                                                                border: `2px solid ${paymentForm.paymentType === 'advance' ? '#1565C0' : '#e5e7eb'}`,
                                                                background: paymentForm.paymentType === 'advance' ? '#eff6ff' : '#fff',
                                                                fontWeight: 700, fontSize: '13px',
                                                                color: paymentForm.paymentType === 'advance' ? '#1e40af' : '#555',
                                                            }}
                                                        >
                                                            Advance Payment
                                                        </button>
                                                    </div>
                                                    {paymentForm.paymentType === 'advance' && invoiceInstallments.length > 0 && enteredAmt > 0 && (
                                                        <div style={{
                                                            marginTop: '10px', background: '#fff7ed', border: '1px solid #fed7aa',
                                                            borderRadius: '8px', padding: '10px 12px', fontSize: '12px', color: '#9a3412'
                                                        }}>
                                                            After this advance, remaining balance <strong>PKR {previewRemaining.toLocaleString()}</strong> will be
                                                            split equally across {invoiceInstallments.length} installment{invoiceInstallments.length === 1 ? '' : 's'}
                                                            (~PKR {previewPerInstallment.toLocaleString()} each).
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {invoiceInstallments.length > 0 && (
                                                <div style={{ marginBottom: '16px' }}>
                                                    <div style={{ fontWeight: 700, fontSize: '12px', color: '#E65100', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '8px' }}>
                                                        Installment Schedule
                                                    </div>
                                                    <div style={{ border: '1px solid #FFD8B0', borderRadius: '8px', overflow: 'hidden' }}>
                                                        {invoiceInstallments.map((row, idx) => (
                                                            <div key={row.id || idx} style={{
                                                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                                padding: '8px 12px', fontSize: '12px',
                                                                borderBottom: idx < invoiceInstallments.length - 1 ? '1px solid #ffe8d1' : 'none',
                                                                background: idx % 2 ? '#FFF8F0' : '#fff',
                                                            }}>
                                                                <div>
                                                                    <strong>#{row.installment_number || idx + 1}</strong>
                                                                    <span style={{ color: '#777', marginLeft: '8px' }}>
                                                                        Due {row.due_date ? String(row.due_date).slice(0, 10) : '—'}
                                                                    </span>
                                                                </div>
                                                                <div style={{ textAlign: 'right' }}>
                                                                    <strong>PKR {Number(row.amount || 0).toLocaleString()}</strong>
                                                                    <div style={{
                                                                        fontSize: '11px', fontWeight: 600,
                                                                        color: row.status === 'Paid' ? '#2E7D32' : row.status === 'Partial' ? '#E65100' : '#666'
                                                                    }}>
                                                                        {row.status || 'Pending'}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {invoicePayments.length > 0 && (
                                                <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', marginBottom: '16px' }}>
                                                    {invoicePayments.map((p, idx) => (
                                                        <div key={p.id} style={{
                                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                            padding: '8px 12px', borderBottom: idx < invoicePayments.length - 1 ? '1px solid #f0f0f0' : 'none',
                                                            fontSize: '12px'
                                                        }}>
                                                            <span>
                                                                {String(p.notes || '').toLowerCase() === 'advance' ? 'Advance' : `#${idx + 1}`}
                                                                {' - '}
                                                                {p.payment_date ? new Date(p.payment_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                                                            </span>
                                                            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                <strong style={{ color: '#16a34a' }}>PKR {Number(p.amount || 0).toLocaleString()}</strong>
                                                                <button type="button" title="View Receipt"
                                                                    onClick={() => navigate(`/payment-receipt/${selectedInvoice.id}/${p.id}`)}
                                                                    style={{ background: '#e8f5e9', color: '#2e7d32', border: 'none', borderRadius: '6px', padding: '4px 6px', cursor: 'pointer' }}>
                                                                    <FontAwesomeIcon icon={faReceipt} />
                                                                </button>
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            <div className="row g-3">
                                                <div className="col-md-4">
                                                    <label className="form-label" style={{ fontSize: '13px', fontWeight: '600' }}>Payment Status</label>
                                                    <select className="form-control" style={{ marginBottom: 0 }}
                                                        value={paymentForm.paymentStatus}
                                                        onChange={e => setPaymentForm({ ...paymentForm, paymentStatus: e.target.value })}>
                                                        <option value="Pending">Pending</option>
                                                        <option value="Paid">Paid</option>
                                                        <option value="Partially Paid">Partially Paid</option>
                                                        <option value="Cancelled">Cancelled</option>
                                                    </select>
                                                </div>
                                                <div className="col-md-4">
                                                    <label className="form-label" style={{ fontSize: '13px', fontWeight: '600' }}>
                                                        {treatingAsAdvance ? 'Advance Amount (PKR)' : 'Payment Amount (PKR)'}
                                                    </label>
                                                    <input type="number" className="form-control" style={{ marginBottom: 0 }}
                                                        placeholder="0.00" value={paymentForm.installmentAmount}
                                                        onChange={e => setPaymentForm({ ...paymentForm, installmentAmount: e.target.value })} />
                                                </div>
                                                <div className="col-md-4">
                                                    <label className="form-label" style={{ fontSize: '13px', fontWeight: '600' }}>Payment Date</label>
                                                    <input type="date" className="form-control" style={{ marginBottom: 0 }}
                                                        value={paymentForm.paymentDate}
                                                        onChange={e => setPaymentForm({ ...paymentForm, paymentDate: e.target.value })} />
                                                </div>
                                            </div>
                                            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                                                {['Paid', 'Partially Paid'].includes(selectedInvoice.payment_status) && Number(selectedInvoice.paid_amount) > 0 && (
                                                    <button type="button" className="btn-secondary"
                                                        onClick={() => {
                                                            if (invoicePayments.length === 1) {
                                                                navigate(`/payment-receipt/${selectedInvoice.id}/${invoicePayments[0].id}`);
                                                            } else if (invoicePayments.length > 1) {
                                                                navigate(`/payment-receipt/${selectedInvoice.id}/${invoicePayments[invoicePayments.length - 1].id}`);
                                                            } else {
                                                                navigate(`/payment-receipt/${selectedInvoice.id}`);
                                                            }
                                                        }}>
                                                        <FontAwesomeIcon icon={faReceipt} style={{ marginRight: '6px' }} />
                                                        View Receipt
                                                    </button>
                                                )}
                                                <button type="button" className="btn-primary"
                                                    onClick={handleUpdateInvoicePayment}
                                                    disabled={updatingPayment}>
                                                    <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '8px' }} />
                                                    {updatingPayment
                                                        ? 'Saving...'
                                                        : (parseFloat(paymentForm.installmentAmount) > 0
                                                            ? (treatingAsAdvance ? 'Record Advance' : 'Record Payment')
                                                            : 'Update Status')}
                                                </button>
                                            </div>
                                        </>
                                        );
                                    })()}
                                </div>
                                <div className="modal-footer" style={{ borderTop: '1px solid #f3f4f6' }}>
                                    <button type="button" className="btn-secondary"
                                        onClick={() => setShowPaymentModal(false)}
                                        disabled={linkingInvoice || updatingPayment}>
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Schedule Meeting Modal */}
            {showMeetingModal && selectedCounselorForMeeting && (
                <>
                    <div className="modal-backdrop show" onClick={() => setShowMeetingModal(false)}></div>
                    <div className="modal show" style={{ display: 'block' }} tabIndex="-1">
                        <div className="modal-dialog modal-dialog-centered modal-lg">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">
                                        <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: '10px' }} />
                                        Schedule Meeting with {selectedCounselorForMeeting.name}
                                    </h5>
                                    <button type="button" className="btn-close-modal" onClick={() => setShowMeetingModal(false)}>
                                        <FontAwesomeIcon icon={faTimes} />
                                    </button>
                                </div>
                                <MeetingScheduler
                                    counselorId={selectedCounselorForMeeting.id}
                                    leadId={id}
                                    onClose={() => setShowMeetingModal(false)}
                                    onSuccess={() => { fetchMeetings(); setShowMeetingModal(false); }}
                                />
                            </div>
                        </div>
                    </div>
                </>
            )}
        </ViewModeWrap>
    );
}
